import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import { buildApiV3UrlWithQuery, buildApiV3Url } from "../shared/Utils";
import {
    parseSuccessResponse,
    parseGetAllObjResponse,
    cannotDeleteRecordResponse,
    parseResponseMessage,
} from "../shared/responseUtils";
import toast from "../hooks/toast";
import { CRUDOperation } from "../shared/constants";

const initialState = {
    isLoading: false,
    isDeleting: false,
    isAddOrEditLoading: false,
    enableLoading: "",
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 0,
            totalRecords: 0,
        },
    },
    sortColumn: ["name", true],
    formState: {
        name: "",
    },
    cannotDeleteRecordMessage: "",
};

export const customerLabelSlice = createSlice({
    name: "customerLabel",
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageToNull: state => {
            state.page = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: 0,
                    totalRecords: 0,
                },
            };
        },
        setIsDeleting: (state, action) => {
            state.isDeleting = action.payload;
        },
        setIsAddOrEditLoading: (state, action) => {
            state.isAddOrEditLoading = action.payload;
        },
        setEnableLoading: (state, action) => {
            state.enableLoading = action.payload;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setFormState: (state, action) => {
            state.formState = action.payload;
        },
        clearFormState: state => {
            state.formState = initialState.formState;
        },
        setCannotDeleteRecordMessage: (state, action) => {
            state.cannotDeleteRecordMessage = action.payload;
        },
    },
});

const [showToast] = toast();
export const {
    setIsLoading,
    setPage,
    setPageToNull,
    setFormState,
    clearFormState,
    setIsDeleting,
    setIsAddOrEditLoading,
    setEnableLoading,
    setSortColumn,
    setCannotDeleteRecordMessage,
} = customerLabelSlice.actions;

export const selectIsLoading = state => {
    return state.customerLabel.isLoading;
};
export const selectPage = state => {
    return state.customerLabel.page;
};
export const selectFormState = state => {
    return state.customerLabel.formState;
};
export const selectIsDeleting = state => {
    return state.customerLabel.isDeleting;
};
export const selectIsAddOrEditLoading = state => {
    return state.customerLabel.isAddOrEditLoading;
};
export const selectEnableLoading = state => {
    return state.customerLabel.enableLoading;
};
export const selectSortColumn = state => {
    return state.customerLabel.sortColumn;
};
export const selectCannotDeleteRecordMessage = state => {
    return state.customerLabel.cannotDeleteRecordMessage;
};

export const fetchCustomerLabels = (top, skip, paginateDetails) => async (dispatch, getState) => {
    const { isLoading, sortColumn, formState } = getState().customerLabel;
    const { rowsPerPage } = getState().session;
    top = !top ? rowsPerPage : top;
    const { name } = formState;

    if (isLoading) {
        return;
    }
    let filterString = "";
    dispatch(setIsLoading(true));
    const sort = `${sortColumn[0]} ${sortColumn[1] ? "ASC" : "DESC"}`;
    if (name) {
        filterString = `name contains '${name}'`;
    }

    const apiURI = buildApiV3UrlWithQuery(`customer-labels`, filterString, null, top, skip, sort);
    const response = await axios.get(`${apiURI}`, { withCredentials: true });

    if (!parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
        dispatch(setPageToNull());
    } else {
        const readAllResponse = parseGetAllObjResponse(response);
        const paginateData = paginateDetails
            ? {
                  pageNumber: paginateDetails.pageNumber,
                  startIndex: paginateDetails.startIndex,
                  endIndex: paginateDetails.endIndex,
                  rowsPerPage: paginateDetails.rowsPerPage,
                  totalRecords: readAllResponse?.count,
              }
            : {
                  pageNumber: 1,
                  startIndex: 0,
                  endIndex: 19,
                  rowsPerPage: 20,
                  totalRecords: readAllResponse?.count,
              };
        dispatch(
            setPage({
                data: readAllResponse?.value,
                paginate: paginateData,
            })
        );
    }
    dispatch(setIsLoading(false));
};

export const deleteCustomerLabel = id => async (dispatch, getState) => {
    const { isDeleting } = getState().customerLabel;
    if (isDeleting) {
        return false;
    }
    let successResponse = true;
    dispatch(setIsDeleting(true));
    const response = await axios
        .delete(buildApiV3Url(`customer-labels/${id}`), {
            withCredentials: true,
        })
        .catch(error => error);
    if (cannotDeleteRecordResponse(response)) {
        dispatch(setCannotDeleteRecordMessage(parseResponseMessage(response)));
        successResponse = false;
    } else if (parseSuccessResponse(CRUDOperation.Delete, response)?.isSuccess) {
        showToast("success", "Label deleted.", "deleteCustomerToast", false);
        dispatch(fetchCustomerLabels());
        dispatch(setCannotDeleteRecordMessage(""));
    }

    dispatch(setIsDeleting(false));
    return successResponse;
};

export const addOrUpdateCustomerLabels =
    (addOrEdit, payload, customerLabelId) => async dispatch => {
        dispatch(setIsAddOrEditLoading(true));
        let success = false;
        if (addOrEdit === "Add") {
            await axios
                .post(buildApiV3Url(`customer-labels`), payload, {
                    withCredentials: true,
                })
                .then(x => {
                    if (parseSuccessResponse(CRUDOperation.Create, x)?.isSuccess) {
                        showToast("success", `Label added.`, "label-success-toast", false);
                        success = true;
                        dispatch(fetchCustomerLabels());
                    } else {
                        success = false;
                    }
                })
                .catch(() => {
                    success = false;
                });
        } else {
            await axios
                .put(buildApiV3Url(`customer-labels/${customerLabelId}`), payload, {
                    withCredentials: true,
                })
                .then(x => {
                    if (parseSuccessResponse(CRUDOperation.Update, x)?.isSuccess) {
                        showToast(
                            "success",
                            `Successfully updated the label`,
                            "customer-label-success-toast",
                            false
                        );
                        success = true;
                        dispatch(fetchCustomerLabels());
                    } else {
                        success = false;
                    }
                })
                .catch(() => {
                    success = false;
                });
        }
        dispatch(setIsAddOrEditLoading(false));
        return success;
    };

export default customerLabelSlice.reducer;
